// Constants
import { KeyCode } from "@ms/uno-constants/lib/local/KeyCode";
import { ComponentNames } from "../../constants/ComponentNames";
// Controls
import { DynamicIcon } from "@ms/uno-controls/lib/local/controls/presentational/dynamicIcon/DynamicIcon";
import { BaseComponent } from "@ms/uno-controls/lib/local/controls/BaseComponent";
import { PlanIcon } from "@ms/uno-controls/lib/local/controls/presentational/planIcon/PlanIcon";
import { SimpleBreadcrumb } from "@ms/uno-controls/lib/local/controls/presentational/simpleBreadcrumb/SimpleBreadcrumb";
import { FocusZone, FocusZoneDirection, IconButton, getRTL } from "@fluentui/react";
// Utilities
import React from "react";
import { KeyboardUtilities } from "@ms/uno-utilities/lib/local/KeyboardUtilities";
import { css } from "@fluentui/utilities/lib/css";
import { Strings } from "../../utilities/CultureUtilities";
// Styles
import { getUnoLeftNavOptionClassNames, getUnoLeftNavOptionStyles, getUnoLeftNavSimpleBreadcrumbStyles } from "./UnoLeftNavOption.styles";
import { moreIconProps } from "@ms/uno-controls/lib/local/icons/icons.styles";
import { withTheme } from "@ms/uno-controls/lib/local/utilities/withTheme";
/**
 * Control for each option in the Uno leftNav
 */ class UnoLeftNavOptionInternal extends BaseComponent {
    /**
     * Name of this component. Used for usage logging.
     */ name() {
        return ComponentNames.UnoLeftNavOption;
    }
    componentDidMount() {
        super.componentDidMount?.();
        if (this.props.onRenderVirtualizedItem) this.props.onRenderVirtualizedItem();
    }
    /**
     * Render the Uno leftNav option
     */ renderComponent() {
        const { ariaPosInSet, ariaSetSize, name, url, id } = this.props;
        const leftNavOptionClassNames = this.getUnoLeftNavOptionClassNames();
        const className = css(this.props.className, leftNavOptionClassNames.root, this.props.selected && "is-selected", this.state.isContextMenuOpen && "is-expanded");
        const icon = this.renderOptionIcon();
        const breadcrumbComponent = this.renderBreadcrumbComponent();
        const contextMenu = this.renderContextMenu();
        //data-tid is used for testing purpose by PAS tool
        const data_tid = `leftNavLink${name.replace(/\s+/g, "")}`;
        return /*#__PURE__*/ React.createElement("li", {
            className: className,
            "aria-label": name,
            "aria-posinset": ariaPosInSet,
            "aria-setsize": ariaSetSize,
            "data-is-focusable": true,
            "data-tid": data_tid,
            id: id,
            tabIndex: 0,
            onClick: this.onListItemClick,
            onKeyDown: this.onListItemKeyDown,
            onFocus: ()=>{
                this.setFocusIcon(true);
            },
            onBlur: ()=>{
                this.setFocusIcon(false);
            },
            onMouseOver: ()=>{
                this.setFocusIcon(true);
            },
            onMouseOut: ()=>{
                this.setFocusIcon(false);
            },
            role: "menuitem"
        }, /*#__PURE__*/ React.createElement("a", {
            href: url,
            "data-is-focusable": "false"
        }, /*#__PURE__*/ React.createElement("div", {
            className: leftNavOptionClassNames.linkContents
        }, icon, /*#__PURE__*/ React.createElement("div", {
            className: css(leftNavOptionClassNames.text)
        }, /*#__PURE__*/ React.createElement("span", {
            className: "leftNavItem",
            title: name
        }, name), breadcrumbComponent), contextMenu)));
    }
    /**
     *  Render the icon for the Uno leftNav option
     */ renderOptionIcon() {
        const { name, icon, containerType, containerPhoto } = this.props;
        const { dynamicIcon, staticIcon } = this.getUnoLeftNavOptionClassNames();
        if (icon != null) {
            const showFocusedIcon = this.props.selected || this.state.displayFocusedIcon || this.state.isContextMenuOpen;
            const iconToRender = icon.focusedIcon && showFocusedIcon ? icon.focusedIcon : icon.normalIcon;
            return /*#__PURE__*/ React.createElement("div", {
                className: css(iconToRender.className, staticIcon)
            }, /*#__PURE__*/ React.createElement(DynamicIcon, {
                focused: (icon.focusedIcon && showFocusedIcon) ?? false,
                stateBasedIcons: icon,
                name: name,
                size: 20,
                iconSvgSize: 20
            }));
        }
        // If there is no icon prop passed in, we can fall back to plan icon rendering.
        return /*#__PURE__*/ React.createElement("div", {
            className: css("icon text-wrapper", dynamicIcon)
        }, /*#__PURE__*/ React.createElement(PlanIcon, {
            containerType: containerType ?? null,
            containerPhoto: containerPhoto ?? null,
            name: name
        }));
    }
    /**
     *  Render breadcrumb component if present
     */ renderBreadcrumbComponent() {
        const { breadcrumbItems, id } = this.props;
        let breadcrumbComponent = null;
        if (breadcrumbItems) {
            breadcrumbComponent = /*#__PURE__*/ React.createElement(SimpleBreadcrumb, {
                keySuffix: id,
                breadcrumbStrings: breadcrumbItems,
                styles: getUnoLeftNavSimpleBreadcrumbStyles(this.props.theme)
            });
        }
        return breadcrumbComponent;
    }
    /**
     * Render context menu if present
     */ renderContextMenu() {
        const { contextMenuItems } = this.props;
        if (contextMenuItems == null) {
            return null;
        }
        const leftNavOptionClassNames = this.getUnoLeftNavOptionClassNames();
        const menuProps = {
            items: contextMenuItems,
            shouldFocusOnMount: true
        };
        return /*#__PURE__*/ React.createElement(FocusZone, {
            direction: FocusZoneDirection.horizontal
        }, /*#__PURE__*/ React.createElement(IconButton, {
            componentRef: this.moreOptionButtonRef,
            className: leftNavOptionClassNames.contextMenuButton,
            menuIconProps: moreIconProps,
            menuProps: menuProps,
            onMenuClick: this.onContextMenuOpen,
            onAfterMenuDismiss: this.onContextMenuDismiss,
            ariaLabel: Strings.UnoLeftNav_MoreOptionsButtonAriaLabel,
            title: Strings.UnoLeftNav_MoreOptionsButtonAriaLabel
        }));
    }
    /**
     * Get the classnames associated with the computed styles for the Uno LeftNav option
     */ getUnoLeftNavOptionClassNames() {
        const { theme, breadcrumbItems } = this.props;
        return getUnoLeftNavOptionClassNames(getUnoLeftNavOptionStyles({
            breadCrumbToDisplay: !!breadcrumbItems && breadcrumbItems.length > 0,
            theme: theme
        }));
    }
    /**
     * Initialize a new instance of UnoLeftNavOption
     * @param props IUnoLeftNavOptionProps object
     * @param context context object to pass through to the base class
     */ constructor(props, context){
        super(props, context), // Reference to the more options button
        this.moreOptionButtonRef = /*#__PURE__*/ React.createRef(), /**
     * Set displayFocusedIcon state for the leftNav option
     * @param displayFocusedIcon value to set
     */ this.setFocusIcon = (displayFocusedIcon)=>{
            this.setState({
                displayFocusedIcon: displayFocusedIcon
            });
        }, /**
     * When the list item is clicked, we want to run the onSelect
     * prop if it's available and no url is given
     */ this.onListItemClick = (event)=>{
            if (this.props.onSelect) {
                event.preventDefault();
                event.stopPropagation();
                this.props.onSelect();
            }
        }, /**
     * Click handler for the anchor element
     * @param e Synthetic keyboard event
     */ this.onListItemKeyDown = (e)=>{
            const keyDowned = KeyboardUtilities.getKey(e);
            if (keyDowned === KeyCode.SPACE) {
                e.preventDefault();
            }
            if (KeyboardUtilities.keyCodeIsSelectionKey(keyDowned, this.props.keyCodeMapper)) {
                if (this.props.onSelect != null) {
                    this.props.onSelect();
                }
            }
            const forwardArrowKey = getRTL() ? KeyCode.LEFT_ARROW : KeyCode.RIGHT_ARROW;
            const backwardArrowKey = getRTL() ? KeyCode.RIGHT_ARROW : KeyCode.LEFT_ARROW;
            if (keyDowned === forwardArrowKey) {
                this.moreOptionButtonRef.current?.focus();
            } else if (keyDowned === backwardArrowKey) {
                e.currentTarget.focus();
            }
        }, /**
     * Open context menu
     */ this.onContextMenuOpen = ()=>{
            this.setState({
                isContextMenuOpen: true
            });
        }, /**
     * Close context menu
     */ this.onContextMenuDismiss = ()=>{
            this.setState({
                isContextMenuOpen: false
            });
        };
        // Initialize state
        this.state = {
            isContextMenuOpen: false,
            displayFocusedIcon: false
        };
    }
}
export const UnoLeftNavOption = withTheme(UnoLeftNavOptionInternal);
